import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';

// components
import SportsWaiver from './SportsWaiver';
import CheckboxWithoutForm from '@atoms/CheckboxWithoutForm';
import { BodyMedium, LinkText } from '@styled';

// functionality
import { CartItemEventContext } from './CartItemEventProvider';
import { LoadingContext } from '@context/LoadingProvider';
import { createCartItem, deleteCartItem } from '@general/api/cartAPI';
import { createEventLineItemPayload } from '@general/helpers/cart.helpers';
import { formatMoney } from '@general/helpers/currency.helpers';
import colors from '@general/colors';
import { formatDateInEastern } from '@general/dates';
import useViewer from '@hooks/useViewer';
import sessionPropTypes from '@react/propTypes/sessionPropTypes';

const SessionInfoTextStyled = styled(LinkText)`
    display: block;
    opacity: 1 !important;

    &.disabled {
        color: ${colors.mediumGray} !important;
    }
`;

const PriceTextStyled = styled(BodyMedium)`
    opacity: 1 !important;

    &.disabled {
        color: ${colors.mediumGray} !important;
    }
`;

const CartItemEventSession = ({
    alreadyRegistered,
    hasConflict,
    session,
    onComponentLoaded = () => {},
    onSessionToggle,
}) => {
    // ------------------------------
    // State
    // ------------------------------

    const [cartItem, setCartItem] = useState();
    const [applicableFee, setApplicableFee] = useState(null);
    const [isSessionSelected, setIsSessionSelected] = useState(false);
    const [sportsWaiverChecked, setSportsWaiverChecked] = useState(false);

    // ------------------------------
    // Hooks
    // ------------------------------

    const { setIsLoading } = useContext(LoadingContext);
    const { eventDataForMember, eventRegistrationData } = useContext(CartItemEventContext);
    const { viewer } = useViewer();

    // ------------------------------
    // Effects
    // ------------------------------

    useEffect(() => {
        if (!eventDataForMember || !applicableFee) {
            return;
        }

        const registeredSession = eventDataForMember.sessions.find(s => s.product_id === applicableFee.id);
        if (registeredSession) {
            setIsSessionSelected(true);
            setSportsWaiverChecked(true);
        }

        setCartItem(registeredSession);
    }, [eventDataForMember, applicableFee]);

    useEffect(() => {
        if (!eventRegistrationData?.registrantClass) {
            return;
        }

        setApplicableFee(session.fees.find(fee => fee.registrant_class === eventRegistrationData.registrantClass[0]));
    }, [eventRegistrationData.registrantClass, session]);

    useEffect(() => {
        if (eventRegistrationData?.cart && applicableFee !== null) {
            onComponentLoaded();
        }
    }, [applicableFee, eventRegistrationData, onComponentLoaded]);

    // ------------------------------
    // Helpers
    // ------------------------------

    const addSessionToCart = async () => {
        setIsLoading(true);
        onSessionToggle(true);

        const cartItemData = [
            createEventLineItemPayload({
                event: eventRegistrationData?.event,
                member: eventRegistrationData?.member,
                registrantClasses: eventRegistrationData?.registrantClass,
                session,
            }),
        ];

        const updatedCart = await createCartItem({
            cartId: eventRegistrationData?.cart.id,
            cartItemData,
            viewerId: viewer.recordId,
        });

        await eventRegistrationData?.refetchCart();

        if (updatedCart === null) {
            setIsLoading(false);
            setIsSessionSelected(false);
            return;
        }

        setCartItem(updatedCart.items.find(registeredSession => registeredSession.product_id === applicableFee.id));
        setIsLoading(false);
    };

    const removeSessionFromCart = async () => {
        setIsLoading(true);
        onSessionToggle(false);

        await deleteCartItem({
            cartId: eventRegistrationData?.cart.id,
            cartItemId: cartItem.id,
            viewerId: viewer.recordId,
        });

        await eventRegistrationData?.refetchCart();

        setIsLoading(false);
    };

    const checkIfSessionCanBeToggled = ({ sessionChecked, waiverChecked }) => {
        const isSessionInCart = !!eventDataForMember.sessions.find(
            registeredSession => registeredSession.product_id === applicableFee.id
        );

        const waiverRequired = applicableFee.sport_waiver_required;

        if (!isSessionInCart) {
            if ((sessionChecked && !waiverRequired) || (waiverRequired && waiverChecked)) {
                addSessionToCart();
            }
        } else if (isSessionInCart) {
            if (!sessionChecked || (waiverRequired && !waiverChecked)) {
                removeSessionFromCart();
            }
        }
    };

    // ------------------------------
    // Render
    // ------------------------------

    if (!eventRegistrationData || !applicableFee) {
        return null;
    }

    const hasSeatsRemaining = session.maximum_attendance > 0 && session.seats_remaining > 0;
    const isSoldOut = session.maximum_attendance > 0 && session.seats_remaining === 0;
    const disabledForConflict = hasConflict && !isSessionSelected;

    return (
        <div className="flex justify-between">
            <div>
                <div className="mb-0.25rem">
                    <CheckboxWithoutForm
                        checked={isSessionSelected}
                        disabled={alreadyRegistered || disabledForConflict || isSoldOut}
                        onCheckChange={checked => {
                            checkIfSessionCanBeToggled({
                                sessionChecked: checked,
                                waiverChecked: checked,
                            });

                            onSessionToggle(checked);
                            setIsSessionSelected(checked);
                            setSportsWaiverChecked(checked);
                        }}
                    >
                        <SessionInfoTextStyled
                            as="span"
                            className={classNames({
                                disabled: disabledForConflict || isSoldOut || alreadyRegistered,
                            })}
                        >
                            {session.title} {alreadyRegistered && '(Already Registered)'}
                        </SessionInfoTextStyled>

                        <SessionInfoTextStyled
                            as="span"
                            className={classNames('text-dark-gray', {
                                disabled: disabledForConflict || isSoldOut || alreadyRegistered,
                            })}
                        >
                            {formatDateInEastern(session.begins_at, 'MM/dd')},{' '}
                            {formatDateInEastern(session.begins_at, 'p')}-{formatDateInEastern(session.ends_at, 'p')}
                        </SessionInfoTextStyled>

                        {hasSeatsRemaining && (
                            <SessionInfoTextStyled
                                as="span"
                                className={classNames('text-dark-gray', {
                                    disabled: disabledForConflict || isSoldOut || alreadyRegistered,
                                })}
                            >
                                Seats Remaining: {session.seats_remaining}
                            </SessionInfoTextStyled>
                        )}

                        {isSoldOut && (
                            <SessionInfoTextStyled
                                as="span"
                                className={classNames('font-bold text-dark-gray', {
                                    disabled: disabledForConflict || isSoldOut,
                                })}
                            >
                                SOLD OUT
                            </SessionInfoTextStyled>
                        )}

                        {disabledForConflict && !alreadyRegistered && (
                            <SessionInfoTextStyled
                                as="span"
                                className={classNames('mt-0.25rem text-dark-gray', {
                                    disabled: disabledForConflict || isSoldOut,
                                })}
                            >
                                This session has been disabled because it conflicts with another session you have
                                selected.
                            </SessionInfoTextStyled>
                        )}
                    </CheckboxWithoutForm>
                </div>

                {applicableFee.sport_waiver_required && (
                    <SportsWaiver
                        checked={sportsWaiverChecked || alreadyRegistered}
                        disabled={alreadyRegistered}
                        onCheckChange={checked => {
                            checkIfSessionCanBeToggled({
                                sessionChecked: isSessionSelected,
                                waiverChecked: checked,
                            });

                            setSportsWaiverChecked(checked);
                            setIsSessionSelected(checked);
                        }}
                    />
                )}
            </div>

            <div>
                <PriceTextStyled
                    className={classNames({
                        disabled: disabledForConflict || isSoldOut || alreadyRegistered,
                    })}
                >
                    {formatMoney(applicableFee.price, eventRegistrationData.cart.locale)}
                </PriceTextStyled>
            </div>
        </div>
    );
};

CartItemEventSession.propTypes = {
    alreadyRegistered: PropTypes.bool,
    hasConflict: PropTypes.bool,
    session: sessionPropTypes,
    onComponentLoaded: PropTypes.func,
    onSessionToggle: PropTypes.func.isRequired,
};

export default CartItemEventSession;
